import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Modal, ModalBody, ModalFooter, ModalHeader, Text, VFlow } from 'bold-ui'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { information } from 'components/modals/information'
import { CidadaoBuscaCpfCnsDnvNisQuery } from 'graphql/types.generated'
import { useState } from 'react'
import { Form, FormRenderProps } from 'react-final-form'
import { useHistory } from 'react-router'

import { CidadaoDocumentoRepetidoCard } from './CidadaoDocumentoRepetidoCard'

export type CidadaoDocumentoRepetidoArray = CidadaoBuscaCpfCnsDnvNisQuery['cidadaosByDocumentos']

interface ModalCidadaosDocumentoRepetidosProps {
  readonly isModalOpen: boolean
  onClose(): void
  onSubmitAtualizarCidadaoDuplicado(): void
  readonly cidadaos: CidadaoDocumentoRepetidoArray
}

interface ModalDuplicidadeCidadaoFormModel {
  readonly cidadaos: CidadaoDocumentoRepetidoArray
}

export function ModalCidadaosDocumentoRepetidos(props: Readonly<ModalCidadaosDocumentoRepetidosProps>) {
  const { isModalOpen, onClose, onSubmitAtualizarCidadaoDuplicado, cidadaos } = props
  const [selectedCidadaoIndex, setSelectedCidadaoIndex] = useState<number>(0)

  const history = useHistory()

  const initialValues: ModalDuplicidadeCidadaoFormModel = {
    cidadaos: cidadaos,
  }

  const handleSubmit = () => {
    onSubmitAtualizarCidadaoDuplicado()

    setTimeout(() => {
      history.push(`${CIDADAO_PATH}/${cidadaos[selectedCidadaoIndex]?.id}/edit`)
    })
  }

  const handleInfoNaoDuplicarCadastroModal = () => {
    information({
      type: 'primary',
      title: 'Preencha com atenção!',
      body:
        'Revise todos os campos já preenchidos e altere, no mínimo, os campos com as mesmas informações, para não duplicar o cadastro.',
      depthLevel: 2,
      manageOverflow: false,
      showCloseButton: true,
      closeLabel: 'Entendi',
    })()
  }

  const handleClose = () => {
    handleInfoNaoDuplicarCadastroModal()
    onClose()
  }

  const renderForm = (formProps: FormRenderProps<ModalDuplicidadeCidadaoFormModel>) => {
    const { values } = formProps
    const cidadaosCount = values.cidadaos?.length

    return (
      <Modal
        size='auto'
        open={isModalOpen}
        closeOnBackdropClick={false}
        onClose={handleClose}
        style={{ width: '45rem' }}
      >
        <ModalHeader
          title={pluralizeTitle(cidadaosCount)}
          subtitle={pluralizeAtualizacaoCadastroMessage(cidadaosCount)}
        />
        <ModalBody>
          <Grid>
            <Cell size={12}>
              <Text fontWeight='bold'>{pluralizeDescription(cidadaosCount)}</Text>
            </Cell>
            <Cell size={12}>
              <VFlow vSpacing={1}>
                {(values.cidadaos || []).map((cidadao, index) => (
                  <CidadaoDocumentoRepetidoCard
                    key={cidadao?.id}
                    cidadao={cidadao}
                    isSelected={index === selectedCidadaoIndex}
                    onSelectCidadao={() => setSelectedCidadaoIndex(index)}
                  />
                ))}
              </VFlow>
            </Cell>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button kind='normal' size='medium' onClick={handleClose}>
              Cadastro novo
            </Button>
            <Button
              kind='primary'
              style={css`
                width: 8rem;
              `}
              onClick={handleSubmit}
              disabled={selectedCidadaoIndex === null}
            >
              Atualizar
            </Button>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<ModalDuplicidadeCidadaoFormModel> onSubmit={handleSubmit} render={renderForm} initialValues={initialValues} />
  )
}

const pluralizeTitle = (numeroCidadaosDuplicados: number) => {
  return numeroCidadaosDuplicados > 1 ? 'Alerta de duplicidade' : 'Cidadão já cadastrado'
}

const pluralizeAtualizacaoCadastroMessage = (numeroCidadaosDuplicados: number) => {
  return numeroCidadaosDuplicados > 1
    ? 'Já existem outros cadastros com estas mesmas informações de identificação. Para evitar duplicação de cadastro você pode escolher um deles na lista abaixo para atualizar.'
    : 'Já existe um cadastro individual com estas mesmas informações de identificação. Informe se deseja cadastrar um novo cidadão ou atualizar este que já existe.'
}

const pluralizeDescription = (numeroCidadaosDuplicados: number) => {
  const prefix = numeroCidadaosDuplicados > 1 ? 'Cadastros encontrados' : 'Cadastro encontrado'
  return `${prefix} para os dados de identificação informados:`
}
